<template>
  <router-view :key="$route.name"></router-view>
</template>

<script>
import { Auth, Hub } from 'aws-amplify';

export default {
  name: 'App',
  created() {
    // Check for OAuth callback when app loads
    this.checkOAuthCallback();
    
    // Listen for OAuth events globally
    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'cognitoHostedUI':
          console.log('OAuth sign-in successful:', data);
          // Store will be updated by the GoogleSignInButton component
          break;
        case 'cognitoHostedUI_failure':
          console.error('OAuth sign-in failed:', data);
          break;
        case 'tokenRefresh_failure':
        case 'signOut':
          this.$store.dispatch('resetState');
          this.$router.push('/login');
          break;
      }
    });
  },
  methods: {
    async checkOAuthCallback() {
      try {
        // Check if there are OAuth parameters in the URL
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('code') || urlParams.has('state')) {
          // Wait a moment for the OAuth flow to complete
          setTimeout(async () => {
            try {
              // Auth.currentAuthenticatedUser() will trigger the OAuth flow completion
              const user = await Auth.currentAuthenticatedUser();
              if (user) {
                // Set default attributes for new Google users
                await this.ensureUserAttributes();
                this.$store.dispatch('fetchUser', user);
                this.$router.push('/gifts');
              }
            } catch (error) {
              console.log('No authenticated user or OAuth callback failed:', error);
            }
          }, 1000);
        }
      } catch (error) {
        console.log('No authenticated user or OAuth callback failed:', error);
        // This is normal if user is not authenticated or OAuth failed
      }
    },

    async ensureUserAttributes() {
      try {
        // Get the current authenticated user
        const user = await Auth.currentAuthenticatedUser();
        console.log('Current user for attribute setting:', user);
        
        // Get current user attributes
        const userAttributes = await Auth.userAttributes(user);
        console.log('Current user attributes:', userAttributes);
        
        const attributeMap = {};
        userAttributes.forEach(attr => {
          attributeMap[attr.Name] = attr.Value;
        });

        // Detect country from URL params or browser locale
        let countryName = 'Australia'; // default
        
        // Check URL parameters first
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('country')) {
          const countryParam = urlParams.get('country').trim();
          if (countryParam.length === 2) {
            countryName = this.expandCountryCode(countryParam.toUpperCase()) || countryName;
          } else {
            countryName = countryParam;
          }
        } else {
          // Fallback to browser locale
          const locale = navigator.language || navigator.userLanguage;
          if (locale) {
            const countryCode = locale.split('-')[1];
            if (countryCode) {
              countryName = this.expandCountryCode(countryCode.toUpperCase()) || countryName;
            }
          }
        }

        // Define default attributes for new users
        const defaultAttributes = {
          'custom:country': countryName,
          'custom:show_mothers_day': 'true',
          'custom:show_fathers_day': 'true',
          'custom:remind_mothers_day': 'true',
          'custom:remind_fathers_day': 'true',
          'custom:remind_christmas': 'true',
          'custom:remind_easter': 'true',
          'custom:remind_valentines': 'true',
          'custom:plan': 'free',
        };

        // Check which attributes are missing and need to be set
        const attributesToSet = {};
        for (const [key, value] of Object.entries(defaultAttributes)) {
          if (!attributeMap[key]) {
            attributesToSet[key] = value;
          }
        }

        // Set missing attributes
        if (Object.keys(attributesToSet).length > 0) {
          await Auth.updateUserAttributes(user, attributesToSet);
        } else {
          console.log('User already has all required custom attributes');
        }

      } catch (error) {
        console.error('Error setting user attributes:', error);
        // Don't throw error - this shouldn't block the login process
      }
    },

    expandCountryCode(code) {
      const countryMap = {
        'AU': 'Australia',
        'BR': 'Brazil',
        'CA': 'Canada',
        'FJ': 'Fiji',
        'FR': 'France',
        'IN': 'India',
        'IE': 'Ireland',
        'MX': 'Mexico',
        'NL': 'Netherlands',
        'NZ': 'New Zealand',
        'PH': 'Philippines',
        'SC': 'Scotland',
        'SG': 'Singapore',
        'ZA': 'South Africa',
        'GB': 'UK',
        'US': 'USA',
        'WA': 'Wales'
      };
      return countryMap[code] || null;
    }
  }
};
</script>

<style>
@import './assets/styles/main.css';
</style>